import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import '../styles/HistoryPage.css';

const HistoryPage = () => {
  return (
    <div className="history-page">
      <div className="page-header">
        <div className="container">
          <Link to="/" className="back-link">
            <ArrowLeft size={20} />
            <span>Back to Home</span>
          </Link>
          <h1 className="hero-medium">
            How Cordyceps Was <span className="accent-orange">First Discovered</span>
          </h1>
          <p className="body-large page-intro">
            Cordyceps was not discovered in a laboratory — it was discovered in the wild, by accident, at extreme altitudes.
          </p>
        </div>
      </div>

      <div className="page-content section-padding">
        <div className="container">
          <div className="content-wrapper">
            <div className="history-image-section">
              <img 
                src="https://images.unsplash.com/photo-1676718912572-b3ebcff192e3" 
                alt="Himalayan mountains where Cordyceps was discovered"
                className="history-image"
              />
            </div>

            <div className="content-block">
              <h2 className="heading-2">The Ancient Discovery</h2>
              <p className="body-regular">
                More than 1,500 years ago, herders in the Himalayan and Tibetan plateau regions noticed something unusual: their yaks, sheep, and goats became stronger, more energetic, and more fertile after grazing in certain high-altitude pastures during the summer.
              </p>
              <p className="body-regular">
                On closer observation, these pastures contained strange, club-like growths emerging from the soil — later understood to be Cordyceps growing naturally from insect hosts underground.
              </p>
              <p className="body-regular">
                This unusual combination of animal vitality + rare natural growth drew the attention of early healers.
              </p>
            </div>

            <div className="content-block">
              <h2 className="heading-2">Entry Into Ancient Medicine</h2>
              <div className="history-two-column">
                <div className="history-text-column">
                  <p className="body-regular">
                    By the Tang Dynasty (7th–10th century CE), Cordyceps had entered the records of traditional Chinese and Tibetan medicine. It was described as a substance that:
                  </p>
                  <ul className="content-list">
                    <li>Restored energy without overstimulation</li>
                    <li>Supported lung and kidney strength</li>
                    <li>Helped the body adapt to cold, fatigue, and altitude</li>
                  </ul>
                </div>
                <div className="history-image-column">
                  <img 
                    src="https://images.unsplash.com/photo-1583466478015-2dce6bf2f551" 
                    alt="Traditional Chinese medicine"
                    className="inline-history-image"
                  />
                </div>
              </div>
              <div className="highlight-box">
                <p className="body-large">
                  Because Cordyceps grew only in remote mountains and could be harvested just once a year, it became known as:
                </p>
                <p className="quote-text accent-orange">"Winter worm, summer grass"</p>
                <p className="body-small">(because it appears as an insect in winter and a plant-like growth in summer)</p>
              </div>
              <p className="body-regular">
                Its rarity made it exclusive to emperors, nobles, and elite warriors. For centuries, common people rarely had access to it.
              </p>
            </div>

            <div className="content-block">
              <h2 className="heading-2">A Mushroom of Kings, Monks, and Warriors</h2>
              <p className="body-regular">Cordyceps was often reserved for:</p>
              <ul className="content-list">
                <li>Chinese emperors seeking longevity and vitality</li>
                <li>Tibetan monks enduring long hours of meditation in thin air</li>
                <li>Elite warriors and messengers who needed stamina and fast recovery</li>
              </ul>
              <p className="body-regular">
                Ancient texts describe it as balancing yin and yang, meaning it was believed to increase strength without causing imbalance or depletion — a key reason it was prized over stimulants.
              </p>
            </div>

            <div className="content-block">
              <h2 className="heading-2">From Legend to Global Attention</h2>
              <p className="body-regular">
                Cordyceps remained largely unknown outside Asia until the late 20th century.
              </p>
              <p className="body-regular">
                A turning point came in the early 1990s, when Chinese female athletes shattered world records in long-distance running. Their coach later mentioned the use of Cordyceps-based tonics as part of their traditional diet.
              </p>
              <p className="body-regular">
                This moment sparked global scientific interest, leading researchers to investigate Cordyceps' role in:
              </p>
              <ul className="content-list">
                <li>Oxygen utilization</li>
                <li>Endurance and fatigue resistance</li>
                <li>Cellular energy production</li>
              </ul>
            </div>

            <div className="content-block">
              <h2 className="heading-2">The Shift to Cordyceps Militaris</h2>
              <p className="body-regular">
                Historically, wild Cordyceps was rare, expensive, and environmentally destructive to harvest. As demand grew, it became clear that wild sourcing was unsustainable and often adulterated.
              </p>
              <p className="body-regular">
                This led to the cultivation of Cordyceps militaris, a species that:
              </p>
              <ul className="content-list">
                <li>Can be grown in controlled, ethical environments</li>
                <li>Contains high levels of cordycepin and bioactive compounds</li>
                <li>Allows consistent quality, safety, and testing</li>
              </ul>
              <p className="body-regular">
                Modern science and cultivation finally made Cordyceps accessible — without compromising nature or purity.
              </p>
            </div>

            <div className="conclusion-block">
              <h2 className="heading-2">Cordyceps Today: Ancient Wisdom, Modern Control</h2>
              <p className="body-large">
                What was once reserved for emperors is now part of a new wellness movement — one that values:
              </p>
              <div className="values-grid">
                <div className="value-item">
                  <span className="accent-orange">Clean energy</span> over stimulants
                </div>
                <div className="value-item">
                  <span className="accent-orange">Adaptation</span> over quick fixes
                </div>
                <div className="value-item">
                  <span className="accent-orange">Transparency</span> over folklore
                </div>
              </div>
              <p className="body-large final-text">
                At its core, Cordyceps represents resilience, balance, and intelligent energy — exactly what modern life demands.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HistoryPage;